import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Confirm from "../../../components/Confirm";
import { Badge, Switch } from "../../../components/ui";
import { FactList } from "../../../components/detail";
import {
  getUserSubscriptionDetails,
  giveUserSubscription,
  revokeUserSubscription,
} from "../../../services/users";
import { timeAgo } from "./accountBadges";

/** Badge of the real subscription state (see afo-back services/subscriptionAdmin.js). */
const STATES = {
  active: { color: "green", label: "Active subscription" },
  canceling: { color: "amber", label: "Cancellation scheduled" },
  past_due: { color: "red", label: "Payment failed" },
  expired: { color: "slate", label: "Expired subscription" },
  none: { color: "slate", label: "No subscription" },
};

const GIFT_MONTHS = [1, 3, 6, 12];

export const SubscriptionStateBadge = ({ state }) => {
  const { t } = useTranslation();
  const { color, label } = STATES[state] || STATES.none;
  return (
    <Badge color={color} dot>
      {t(label)}
    </Badge>
  );
};

/** "Visa •••• 4242", "CinetPay · OM"… */
const paymentMeans = (payment) =>
  [
    payment.paymentMethod,
    payment.cinetpayPaymentMethod,
    payment.brand && payment.last4 ? `${payment.brand} •••• ${payment.last4}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Real subscription of a user in the back-office: state, dates, provider and payments, with a
 * switch that actually removes the subscription (and stops Stripe billing) or offers months.
 * `onChange` is called after a change so the page can reload the user.
 */
const UserSubscription = ({ userId, onChange }) => {
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // "revoke" | "give"
  const [months, setMonths] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setDetails(await getUserSubscriptionDetails(userId));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString(i18n.language, {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : undefined;

  const revoke = async () => {
    try {
      const result = await revokeUserSubscription(userId);
      toast.success(result.message || t("Subscription removed"));
      setDetails(result.data);
      onChange?.();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const give = async () => {
    try {
      const data = await giveUserSubscription(userId, "monthly", months);
      // Abonné par carte : l'API précise que la prochaine échéance est décalée
      toast.success(data?.data?.mode === "card_postponed" ? data.message : t("Subscription offered"));
      await load();
      onChange?.();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading && !details) {
    return <div className="ui-skeleton" style={{ height: 120 }} />;
  }
  if (!details) {
    return <p className="ui-cell-muted">{t("Subscription details unavailable")}</p>;
  }

  const { state, isActive, userFlag, subscription, payments } = details;
  const end = subscription?.end;
  const endLabel =
    state === "expired"
      ? t("Expired on")
      : state === "canceling"
      ? t("Access until")
      : t("Next due date");

  return (
    <div className="user-subscription">
      <div className="user-setting">
        <div>
          <h3 className="detail-section-title">{t("Subscription")}</h3>
          <SubscriptionStateBadge state={state} />
          {userFlag === "paid" && !isActive && (
            <p className="user-subscription-warning">
              {t("This user was still shown as paying although the subscription has ended.")}
            </p>
          )}
        </div>
        <Switch
          checked={isActive}
          onChange={(checked) => setPending(checked ? "give" : "revoke")}
          label={t("Subscription")}
          onLabel={t("Paid")}
          offLabel={t("Free")}
        />
      </div>

      {subscription && (
        <FactList
          plain
          items={[
            { label: t("Payment provider"), value: subscription.providerLabel },
            {
              label: t("Plan"),
              value: subscription.type === "annual" ? t("Annual") : t("Monthly"),
            },
            { label: t("Started on"), value: formatDate(subscription.start) },
            {
              label: endLabel,
              value: end ? `${formatDate(end)} (${timeAgo(end, i18n.language)})` : undefined,
            },
            {
              label: t("Automatic renewal"),
              value: subscription.stripeSubscriptionId
                ? state === "active" || state === "past_due"
                  ? t("Yes (Stripe)")
                  : t("No")
                : t("No (prepaid)"),
            },
            ...(subscription.stripeSubscriptionId
              ? [{ label: t("Stripe ID"), value: subscription.stripeSubscriptionId }]
              : []),
          ]}
        />
      )}

      <h3 className="detail-section-title user-subscription-payments-title">
        {t("Payments")}
      </h3>
      {payments.length === 0 ? (
        <p className="ui-cell-muted">{t("No payment")}</p>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Date")}</th>
                <th>{t("Type")}</th>
                <th>{t("Amount")}</th>
                <th>{t("Payment method")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment._id}>
                  <td className="ui-cell-muted">
                    {formatDate(payment.paymentDate || payment.createdAt)}
                  </td>
                  <td>
                    {t(payment.paymentType || "—")}
                    {payment.subscriptionType ? ` · ${t(payment.subscriptionType)}` : ""}
                  </td>
                  <td>
                    {payment.amount} {String(payment.currency || "").toUpperCase()}
                  </td>
                  <td className="ui-cell-muted">{paymentMeans(payment)}</td>
                  <td>
                    <Badge
                      color={
                        payment.paymentStatus === "SUCCESS"
                          ? "green"
                          : payment.paymentStatus === "FAILED"
                          ? "red"
                          : "amber"
                      }
                    >
                      {t(payment.paymentStatus || "PENDING")}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending === "revoke" && (
        <Confirm open setOpen={() => setPending(null)} confirmHandle={revoke}>
          <div className="model-wrapper">
            <p>{t("Remove this user's subscription? Premium access ends immediately.")}</p>
            {subscription?.stripeSubscriptionId && isActive && (
              <p>
                {t(
                  "The Stripe subscription will be cancelled: the user will no longer be charged. No automatic refund."
                )}
              </p>
            )}
          </div>
        </Confirm>
      )}
      {pending === "give" && (
        <Confirm open setOpen={() => setPending(null)} confirmHandle={give}>
          <div className="model-wrapper">
            <p>{t("Offer a subscription to this user?")}</p>
            <label className="user-subscription-months">
              <span>{t("Duration")}</span>
              <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
                {GIFT_MONTHS.map((value) => (
                  <option key={value} value={value}>
                    {t("monthsCount", { count: value })}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Confirm>
      )}
    </div>
  );
};

export default UserSubscription;
